import { Global } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Backdrop } from './Backdrop/Backdrop';
import { Content } from './Content/Content';
import { Header } from './Header/Header';
import { Sidebars } from './Sidebars/Sidebars';
import { S } from './SqwLayout.styles';
import type { SqwLayoutProps } from './SqwLayout.types';
import { type BreadcrumbOverride, SqwLayoutContext } from './SqwLayoutContext';

export const SqwLayout = ({ header, sidebar, content, desktopSidebarTogglerMode = 'none' }: SqwLayoutProps) => {
  // Desktop sidebar starts expanded (visible); users can collapse it via the toggle button
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(desktopSidebarTogglerMode !== 'none');
  const [isMobileSidebarExpanded, setIsMobileSidebarExpanded] = useState(false);

  // Safety: If backend switches mode to 'none' at runtime, ensure sidebar is expanded (no toggle button to recover)
  useEffect(() => {
    if (desktopSidebarTogglerMode === 'none') setIsSidebarExpanded(true);
  }, [desktopSidebarTogglerMode]);

  const [breadcrumbOverride, setBreadcrumbOverride] = useState<BreadcrumbOverride | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null);
  const contentScrollerRef = useRef<HTMLElement | null>(null);

  const isSidebarCollapsed = desktopSidebarTogglerMode === 'fully-collapse' && !isSidebarExpanded;

  const { breadcrumb, ...contentProps } = content || {};

  const contextValue = useMemo(
    () => ({
      isSidebarExpanded,
      setIsSidebarExpanded,
      isMobileSidebarExpanded,
      setIsMobileSidebarExpanded,
      setBreadcrumbOverride,
      canScrollUp,
      canScrollDown,
      scrollDirection,
      desktopSidebarTogglerMode,
      isSidebarCollapsed,
      contentScrollerRef,
    }),
    [
      isSidebarExpanded,
      isMobileSidebarExpanded,
      canScrollUp,
      canScrollDown,
      scrollDirection,
      desktopSidebarTogglerMode,
      isSidebarCollapsed,
    ],
  );

  return (
    <SqwLayoutContext.Provider value={contextValue}>
      <Global styles={S.globalStyles} />
      <S.LayoutContainer isSidebarCollapsed={isSidebarCollapsed}>
        <Header {...header} />
        <Sidebars header={header} sidebar={sidebar} />
        <Content
          {...contentProps}
          breadcrumb={breadcrumbOverride || breadcrumb}
          onCanScrollDownChange={setCanScrollDown}
          onCanScrollUpChange={setCanScrollUp}
          onScrollDirectionChange={setScrollDirection}
          scrollerRef={contentScrollerRef}
        />
        <Backdrop />
      </S.LayoutContainer>
    </SqwLayoutContext.Provider>
  );
};
